import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Terminal, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function Join() {
  const { code } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`/invites/${code}`)
      .then(r => setInvite(r.data))
      .catch(e => setError(e?.response?.data?.detail || "Invite invalid"));
  }, [code]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/join", { code, ...form });
      localStorage.setItem("dara_token", data.token);
      toast.success(`Welcome to ${data.user.company}.`);
      window.location.href = "/app";
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Join failed");
    }
    setLoading(false);
  };

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-6" data-testid="join-page-error">
        <div className="panel p-10 max-w-md text-center">
          <h1 className="font-heading font-black text-3xl mb-3">Invite invalid</h1>
          <p className="text-sm text-zinc-500 mb-6">{error}</p>
          <Link to="/" className="font-mono text-[11px] uppercase text-[#FFB000]">← Home</Link>
        </div>
      </div>
    );
  }
  if (!invite) return <div className="min-h-screen bg-black text-zinc-500 font-mono flex items-center justify-center text-xs">LOADING INVITE…</div>;

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-6" data-testid="join-page">
      <div className="w-full max-w-md panel">
        <div className="border-b border-zinc-900 px-6 py-3 flex items-center gap-2">
          <Terminal className="w-4 h-4 text-[#FFB000]" />
          <span className="font-heading font-black tracking-tighter">DARA</span>
          <span className="font-mono text-[10px] text-zinc-500 ml-auto">INVITE ACCEPTED</span>
        </div>
        <form onSubmit={submit} className="p-8 space-y-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">YOU ARE JOINING</div>
            <h1 className="font-heading font-black text-3xl tracking-tighter uppercase">{invite.workspace.name}</h1>
            <div className="font-mono text-[11px] text-zinc-500 mt-1">{invite.workspace.industry} · as {invite.invite.role}</div>
          </div>

          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Full name</span>
            <input data-testid="join-name" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className={fieldCls} />
          </label>
          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Email</span>
            <input data-testid="join-email" type="email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className={fieldCls} />
          </label>
          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Password (min 6)</span>
            <input data-testid="join-password" type="password" required minLength={6} value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} className={fieldCls} />
          </label>

          <button
            type="submit"
            disabled={loading}
            data-testid="join-submit-btn"
            className="w-full bg-[#FFB000] text-black font-bold font-mono text-xs uppercase tracking-wider py-3 hover:bg-[#E59E00] transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {loading ? "JOINING…" : <>Join terminal <ArrowRight className="w-4 h-4" /></>}
          </button>
        </form>
      </div>
    </div>
  );
}

const fieldCls = "mt-2 w-full bg-black border border-zinc-800 px-3 py-2.5 font-mono text-sm text-white focus:outline-none focus:border-[#FFB000]";
